import { StrictMode } from 'react';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { KindeProvider } from '@kinde-oss/kinde-auth-react';

// Archivo, weights 400 / 600 / 800 only — self-hosted, so there is no third-party font
// round trip on first paint (README § Design Tokens). Latin subset only: the product is US
// English throughout, including AI-generated copy (BUSINESS_RULES § Locale).
import '@fontsource/archivo/latin-400.css';
import '@fontsource/archivo/latin-600.css';
import '@fontsource/archivo/latin-800.css';

import '@/styles/tokens.css';
import '@/styles/base.css';

import { router } from '@/router';
import { SessionProvider } from '@/session/SessionProvider';
import {
  KINDE_AUDIENCE,
  KINDE_CLIENT_ID,
  KINDE_DOMAIN,
  KINDE_SCOPE,
  logoutUri,
  redirectUri,
} from '@/lib/kinde';
import { setTokenGetter } from '@/lib/api';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Meeting data is processed upstream and does not change under the user mid-session;
      // holding it briefly keeps back-navigation instant instead of re-fetching.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * One canonical origin.
 *
 * The platform's generated hostname cannot be turned off, and its routing rules cannot match on
 * host — so the redirect happens here, before React mounts and before anything is stored.
 *
 * Two live URLs for one app is not a cosmetic problem. Sessions, tokens and `localStorage` are
 * scoped per origin, so a person signed in on one hostname is signed out on the other, with no
 * visible reason. Identity and payment redirects are registered per exact URL, so whichever
 * origin was not registered fails at the worst moment — after the provider has taken over. And
 * a link shared from the wrong hostname keeps that split alive indefinitely.
 *
 * Matched exactly rather than by `.azurestaticapps.net` suffix, so per-branch preview
 * environments keep working — those are supposed to be a separate origin.
 *
 * `replace`, not `assign`: the old URL must not survive in history, or Back returns to it and
 * bounces again.
 */
const LEGACY_HOST = 'ashy-dune-00bad6b0f.7.azurestaticapps.net';
const CANONICAL_HOST = 'app.aware-insights.net';

if (window.location.hostname === LEGACY_HOST) {
  const { pathname, search, hash } = window.location;
  window.location.replace(`https://${CANONICAL_HOST}${pathname}${search}${hash}`);
}

const container = document.getElementById('root');
if (!container) throw new Error('#root missing from index.html');

/**
 * Hands the API client a way to fetch the current access token.
 *
 * Inverted deliberately: `lib/api.ts` must not import the Kinde SDK. Keeping the dependency
 * one-directional means the whole data layer stays testable without an auth provider, and the
 * identity vendor stays swappable from one file — the same reason the API client already knows
 * nothing about which component called it.
 */
function TokenBridge({ children }: { children: React.ReactNode }) {
  const { getAccessToken } = useKindeAuth();
  setTokenGetter(getAccessToken);
  return <>{children}</>;
}

createRoot(container).render(
  <StrictMode>
    <KindeProvider
      domain={KINDE_DOMAIN}
      clientId={KINDE_CLIENT_ID}
      redirectUri={redirectUri()}
      logoutUri={logoutUri()}
      audience={KINDE_AUDIENCE}
      scope={KINDE_SCOPE}
      // `useInsecureForRefreshToken` is deliberately NOT set. We authenticate against a custom
      // domain (auth.awarenow.ai), so the SDK keeps the refresh token in a first-party cookie
      // rather than localStorage — a refresh token in localStorage is readable by any script
      // that gets onto the page.
    >
      <QueryClientProvider client={queryClient}>
        <TokenBridge>
          <SessionProvider>
            <RouterProvider router={router} />
          </SessionProvider>
        </TokenBridge>
      </QueryClientProvider>
    </KindeProvider>
  </StrictMode>,
);
